import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Box, Layers, Terminal, Globe, Zap, AlertCircle, CheckCircle, ArrowRight, ExternalLink, Package, Code2, Cpu } from 'lucide-react';
import LabNavigation from '@/components/LabNavigation';

interface PackageInfo {
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  features: string[];
}

const packages: PackageInfo[] = [
  {
    name: "@omnimcp/client",
    description: "Node.js MCP client with AI integration",
    icon: Terminal,
    color: "#4EC9B0",
    features: ["OpenAI & Anthropic support", "Automatic retries", "Event-driven architecture"]
  },
  {
    name: "@omnimcp/sdk",
    description: "Browser-compatible SDK for web apps",
    icon: Globe,
    color: "#569CD6",
    features: ["Works in modern browsers", "TypeScript-first", "Minimal bundle size"]
  },
  {
    name: "@omnimcp/cli",
    description: "Command-line interface & playground",
    icon: Box,
    color: "#DCDCAA",
    features: ["VS Code-inspired UI", "Interactive testing", "Real-time debugging"]
  },
  {
    name: "@omnimcp/core",
    description: "Shared types, utilities & error handling",
    icon: Layers,
    color: "#C586C0",
    features: ["Type definitions", "Error utilities", "Common helpers"]
  },
];

const errorExamples = [
  {
    before: {
      error: "ECONNREFUSED",
      message: "Connection refused"
    },
    after: {
      error: "Server Connection Failed",
      message: "Could not connect to MCP server at localhost:3000",
      suggestion: "Check if the server is running with: mcp-server start"
    }
  },
  {
    before: {
      error: "TOOL_NOT_FOUND",
      message: "Tool not found"
    },
    after: {
      error: "Unknown Tool",
      message: "Tool 'create_user' is not registered on this server",
      suggestion: "Available tools: get_user, list_users, update_user"
    }
  },
  {
    before: {
      error: "INVALID_PARAMS",
      message: "Invalid parameters"
    },
    after: {
      error: "Parameter Validation Failed",
      message: "Missing required field 'email' in tool call",
      suggestion: "Expected: { name: string, email: string, age?: number }"
    }
  },
];

const OmniMcpStudy = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [activePackage, setActivePackage] = useState(0);
  const [activeError, setActiveError] = useState(0);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveError((prev) => (prev + 1) % errorExamples.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#1e1e1e] text-[#d4d4d4] font-mono">
      {/* VS Code-like top bar */}
      <div className="fixed top-0 left-0 right-0 h-8 bg-[#323233] border-b border-[#3c3c3c] flex items-center px-4 z-50">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
          <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
          <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
        </div>
        <div className="flex-1 text-center text-xs text-[#8c8c8c]">
          OmniMCP - The Universal Bridge
        </div>
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="fixed top-12 left-4 z-40 flex items-center gap-2 text-[#8c8c8c] hover:text-[#d4d4d4] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </motion.button>

      {/* Hero */}
      <section className="min-h-screen flex flex-col justify-center items-center px-8 pt-20 relative">
        {/* Network visualization background */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <svg className="w-full h-full">
            {[...Array(20)].map((_, i) => (
              <motion.circle
                key={i}
                cx={`${Math.random() * 100}%`}
                cy={`${Math.random() * 100}%`}
                r="2"
                fill="#569CD6"
                animate={{
                  opacity: [0.3, 1, 0.3],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </svg>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl relative z-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#264f78] rounded text-[#569CD6] text-sm mb-8">
            <Cpu className="w-4 h-4" />
            <span>MCP Client Library</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-[#4EC9B0]">Omni</span>
            <span className="text-[#d4d4d4]">MCP</span>
          </h1>

          <p className="text-xl text-[#8c8c8c] mb-4 max-w-2xl mx-auto">
            The universal MCP client that connects any AI to any tool.
          </p>
          <p className="text-[#569CD6] text-lg mb-12">
            One client. Every transport. All providers.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 text-sm mb-12">
            {[
              { label: "Packages", value: "4" },
              { label: "Transports", value: "All" },
              { label: "Providers", value: "OpenAI + Anthropic + Custom" },
            ].map((stat, i) => (
              <div key={i} className="px-4 py-2 bg-[#2d2d2d] rounded border border-[#3c3c3c]">
                <span className="text-[#569CD6]">{stat.value}</span>
                <span className="text-[#8c8c8c] ml-2">{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Install Command */}
          <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#6A9955]">$</span>
              <span className="text-[#d4d4d4]">npm install</span>
              <span className="text-[#ce9178]">omnimcp</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* The Problem */}
      <section className="py-20 px-8 bg-[#252526]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-[#d4d4d4] mb-8">
              <span className="text-[#C586C0]">The</span> Problem
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-6">
                <AlertCircle className="w-8 h-8 text-[#f14c4c] mb-4" />
                <h3 className="text-[#d4d4d4] font-semibold mb-2">Fragmented Ecosystem</h3>
                <p className="text-[#8c8c8c] text-sm">
                  MCP servers use different transports (stdio, SSE, HTTP). Each needs different client code.
                </p>
              </div>
              <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-6">
                <AlertCircle className="w-8 h-8 text-[#f14c4c] mb-4" />
                <h3 className="text-[#d4d4d4] font-semibold mb-2">Cryptic Errors</h3>
                <p className="text-[#8c8c8c] text-sm">
                  "ECONNREFUSED" tells you nothing. Developers waste hours debugging connection issues.
                </p>
              </div>
              <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-6">
                <AlertCircle className="w-8 h-8 text-[#f14c4c] mb-4" />
                <h3 className="text-[#d4d4d4] font-semibold mb-2">Provider Lock-in</h3>
                <p className="text-[#8c8c8c] text-sm">
                  Code written for OpenAI doesn't work with Anthropic. Switching costs are high.
                </p>
              </div>
              <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-6">
                <AlertCircle className="w-8 h-8 text-[#f14c4c] mb-4" />
                <h3 className="text-[#d4d4d4] font-semibold mb-2">No Good Testing Story</h3>
                <p className="text-[#8c8c8c] text-sm">
                  Testing MCP integrations means spinning up real servers. Slow and flaky.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Architecture */}
      <section className="py-20 px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-[#d4d4d4] mb-4">
              <span className="text-[#4EC9B0]">Modular</span> Architecture
            </h2>
            <p className="text-[#8c8c8c]">
              Four packages. Use what you need. Tree-shake the rest.
            </p>
          </motion.div>

          {/* Package Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActivePackage(index)}
                className={`bg-[#1e1e1e] border rounded-lg p-6 cursor-pointer transition-all ${
                  activePackage === index
                    ? 'border-[#569CD6] shadow-lg shadow-[#569CD6]/10'
                    : 'border-[#3c3c3c] hover:border-[#4c4c4c]'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${pkg.color}20` }}
                  >
                    <pkg.icon className="w-5 h-5" style={{ color: pkg.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1" style={{ color: pkg.color }}>
                      {pkg.name}
                    </h3>
                    <p className="text-[#8c8c8c] text-sm mb-3">{pkg.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {pkg.features.map((feature, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-[#2d2d2d] rounded text-xs text-[#8c8c8c]"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Connection Visualization */}
          <div className="flex justify-center items-center gap-4 text-[#8c8c8c] text-sm">
            <span className="px-3 py-1 bg-[#2d2d2d] rounded">Your App</span>
            <ArrowRight className="w-4 h-4" />
            <span className="px-3 py-1 bg-[#264f78] text-[#569CD6] rounded">OmniMCP</span>
            <ArrowRight className="w-4 h-4" />
            <span className="px-3 py-1 bg-[#2d2d2d] rounded">Any MCP Server</span>
          </div>
        </div>
      </section>

      {/* Error Handling Showcase */}
      <section className="py-20 px-8 bg-[#252526]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-[#d4d4d4] mb-4">
              <span className="text-[#DCDCAA]">Actionable</span> Error Messages
            </h2>
            <p className="text-[#8c8c8c]">
              Errors that tell you what went wrong AND how to fix it.
            </p>
          </motion.div>

          {/* Before/After Error Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg overflow-hidden">
              <div className="bg-[#3c3c3c] px-4 py-2 text-xs text-[#8c8c8c] flex items-center gap-2">
                <AlertCircle className="w-3 h-3 text-[#f14c4c]" />
                Without OmniMCP
              </div>
              <div className="p-4 font-mono text-sm">
                <motion.div
                  key={`before-${activeError}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[#f14c4c]"
                >
                  <p>Error: {errorExamples[activeError].before.error}</p>
                  <p className="text-[#8c8c8c] mt-2">{errorExamples[activeError].before.message}</p>
                </motion.div>
              </div>
            </div>

            {/* After */}
            <div className="bg-[#1e1e1e] border border-[#27c93f] rounded-lg overflow-hidden">
              <div className="bg-[#27c93f]/20 px-4 py-2 text-xs text-[#27c93f] flex items-center gap-2">
                <CheckCircle className="w-3 h-3" />
                With OmniMCP
              </div>
              <div className="p-4 font-mono text-sm">
                <motion.div
                  key={`after-${activeError}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-[#DCDCAA]">{errorExamples[activeError].after.error}</p>
                  <p className="text-[#d4d4d4] mt-2">{errorExamples[activeError].after.message}</p>
                  <p className="text-[#6A9955] mt-2">
                    💡 {errorExamples[activeError].after.suggestion}
                  </p>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Error navigation dots */}
          <div className="flex justify-center gap-2 mt-6">
            {errorExamples.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveError(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  activeError === i ? 'bg-[#569CD6]' : 'bg-[#3c3c3c]'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Code Example */}
      <section className="py-20 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-[#d4d4d4] mb-4">
              <span className="text-[#569CD6]">Simple</span> API
            </h2>
          </motion.div>

          <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg overflow-hidden">
            <div className="bg-[#2d2d2d] px-4 py-2 text-xs text-[#8c8c8c] flex items-center gap-2">
              <Code2 className="w-3 h-3" />
              example.ts
            </div>
            <pre className="p-4 text-sm overflow-x-auto">
              <code>
                <span className="text-[#C586C0]">import</span>
                <span className="text-[#d4d4d4]">{" { MCPClient } "}</span>
                <span className="text-[#C586C0]">from</span>
                <span className="text-[#ce9178]"> 'omnimcp'</span>
                <span className="text-[#d4d4d4]">;</span>
                {"\n\n"}
                <span className="text-[#6A9955]">// Connect to any MCP server</span>
                {"\n"}
                <span className="text-[#569CD6]">const</span>
                <span className="text-[#d4d4d4]"> client = </span>
                <span className="text-[#569CD6]">new</span>
                <span className="text-[#4EC9B0]"> MCPClient</span>
                <span className="text-[#d4d4d4]">();</span>
                {"\n\n"}
                <span className="text-[#C586C0]">await</span>
                <span className="text-[#d4d4d4]"> client.</span>
                <span className="text-[#DCDCAA]">connect</span>
                <span className="text-[#d4d4d4]">({"{"}</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"  "}transport: </span>
                <span className="text-[#ce9178]">'stdio'</span>
                <span className="text-[#d4d4d4]">,</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"  "}command: </span>
                <span className="text-[#ce9178]">'node server.js'</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"})"}</span>
                <span className="text-[#d4d4d4]">;</span>
                {"\n\n"}
                <span className="text-[#6A9955]">// Call tools with full type safety</span>
                {"\n"}
                <span className="text-[#569CD6]">const</span>
                <span className="text-[#d4d4d4]"> result = </span>
                <span className="text-[#C586C0]">await</span>
                <span className="text-[#d4d4d4]"> client.</span>
                <span className="text-[#DCDCAA]">callTool</span>
                <span className="text-[#d4d4d4]">(</span>
                <span className="text-[#ce9178]">'calculator'</span>
                <span className="text-[#d4d4d4]">, {"{"}</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"  "}a: </span>
                <span className="text-[#b5cea8]">5</span>
                <span className="text-[#d4d4d4]">,</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"  "}b: </span>
                <span className="text-[#b5cea8]">3</span>
                {"\n"}
                <span className="text-[#d4d4d4]">{"});"}</span>
                {"\n\n"}
                <span className="text-[#d4d4d4]">console.</span>
                <span className="text-[#DCDCAA]">log</span>
                <span className="text-[#d4d4d4]">(result); </span>
                <span className="text-[#6A9955]">// {"{ result: 8 }"}</span>
              </code>
            </pre>
          </div>
        </div>
      </section>

      {/* Playground Teaser */}
      <section className="py-20 px-8 bg-[#252526]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Terminal className="w-12 h-12 text-[#569CD6] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#d4d4d4] mb-4">
              VS Code-Inspired Playground
            </h2>
            <p className="text-[#8c8c8c] mb-8 max-w-xl mx-auto">
              Test MCP servers interactively without writing code.
              Real-time debugging, tool discovery, and response inspection.
            </p>
            <a
              href="https://omnimcp.ddiy.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0e639c] text-white rounded font-semibold hover:bg-[#1177bb] transition-colors"
            >
              Try the Playground
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold text-[#d4d4d4] mb-8">
              Start Building
            </h2>

            <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-4 mb-8 font-mono">
              <span className="text-[#6A9955]">$</span>{" "}
              <span className="text-[#d4d4d4]">npm install</span>{" "}
              <span className="text-[#ce9178]">omnimcp</span>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://omnimcp.ddiy.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0e639c] text-white rounded font-semibold hover:bg-[#1177bb] transition-colors"
              >
                Documentation
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://github.com/josharsh/omnimcp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2d2d2d] text-[#d4d4d4] rounded font-semibold hover:bg-[#3c3c3c] transition-colors border border-[#3c3c3c]"
              >
                <Package className="w-4 h-4" />
                GitHub
              </a>
            </div>

            <p className="mt-12 text-[#8c8c8c] text-sm">
              Built by <a href="/" className="text-[#569CD6] hover:underline">Harsh Joshi</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="omnimcp" variant="dark" />
    </div>
  );
};

export default OmniMcpStudy;
